import React, { useContext, useEffect, useRef, useState } from 'react';
import { Avatar, Dropdown, MenuProps, Space, Spin } from 'antd';
import { CheckOutlined, DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { OrganizationMembershipResponse } from '../../api-client';
import { DentobookApplicationContext } from '../../context/DentobookApplicationContext';
import { getAuthenticationApi } from '../../api/apiInstances';
import { tokenService } from '../../services/tokenService';
import logger from '../../utils/logger';

/**
 * OrgSwitcher — shown only when the logged-in user belongs to more than one
 * active organisation.  Calls POST /api/auth/select-org and performs a full
 * page reload so that all org-scoped state (context, caches, API data) is
 * guaranteed to be fresh after a switch.
 */
const OrgSwitcher: React.FC = () => {
    const intl = useIntl();
    const { userOrganization } = useContext(DentobookApplicationContext);
    const [orgs, setOrgs] = useState<OrganizationMembershipResponse[]>([]);
    const [switching, setSwitching] = useState(false);
    // Prevent re-running the guard switch more than once per mount.
    const guardFired = useRef(false);

    useEffect(() => {
        // Wait until the current org is resolved from the JWT before fetching.
        if (!userOrganization?.id) return;

        getAuthenticationApi()
            .getUserOrganizations()
            .then(async res => {
                const active = (res.data as OrganizationMembershipResponse[]).filter(
                    o => o.status?.toLowerCase() === 'active'
                );
                setOrgs(active);

                // Guard: if the current org is no longer in the active list
                // (membership was removed/suspended), silently switch to the
                // first available active org so the user isn't stuck.
                if (
                    !guardFired.current &&
                    active.length > 0 &&
                    !active.find(o => o.organizationId === userOrganization.id)
                ) {
                    guardFired.current = true;
                    logger.warn('Current org no longer active; auto-switching to first available org.');
                    setSwitching(true);
                    try {
                        const switchRes = await getAuthenticationApi().selectOrganization({
                            organizationId: active[0].organizationId!,
                        });
                        const rememberMe = localStorage.getItem('dentobook_remember_me') === 'true';
                        tokenService.setTokens(
                            switchRes.data.access_token,
                            switchRes.data.refresh_token,
                            rememberMe
                        );
                        // Write flash before redirect so DesktopLayout/MobileLayout
                        // shows the revoke-banner after the page reloads.
                        sessionStorage.setItem('dentobook_session_event', JSON.stringify({
                            type: 'org_switched_after_revoke',
                            orgName: active[0].organizationName,
                        }));
                        window.location.href = '/';
                    } catch (err) {
                        logger.error('Auto-switch failed:', err);
                        setSwitching(false);
                    }
                }
            })
            .catch(err => logger.error('Failed to load organisations for switcher:', err));
    }, [userOrganization?.id]);

    // Hide for single-org users — no cost, no noise.
    if (orgs.length <= 1) return null;

    const currentOrgId = userOrganization?.id;

    const handleSwitch = async (orgId: string) => {
        if (orgId === currentOrgId || switching) return;
        setSwitching(true);
        try {
            const res = await getAuthenticationApi().selectOrganization({ organizationId: orgId });
            const rememberMe = localStorage.getItem('dentobook_remember_me') === 'true';
            tokenService.setTokens(res.data.access_token, res.data.refresh_token, rememberMe);
            // Full reload so all org-scoped state is guaranteed fresh after switching.
            window.location.href = '/';
        } catch (err) {
            logger.error('Failed to switch organisation:', err);
            setSwitching(false);
        }
    };

    const currentOrg = orgs.find(o => o.organizationId === currentOrgId);
    const displayName = currentOrg?.organizationName || userOrganization?.name || '';

    // Show avatar image if available; fallback to letter Avatar.
    const renderOrgAvatar = (url: string | null | undefined, name: string) => {
        if (url) {
            return (
                <img
                    src={url}
                    width={24}
                    height={24}
                    style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    alt=""
                />
            );
        }
        return (
            <Avatar size="small" style={{ backgroundColor: '#1677ff', flexShrink: 0 }}>
                {(name || '?')[0].toUpperCase()}
            </Avatar>
        );
    };

    const menuItems: MenuProps['items'] = orgs.map(org => ({
        key: org.organizationId!,
        label: (
            <Space style={{ padding: '2px 0' }}>
                {renderOrgAvatar(org.avatarUrl, org.organizationName || '')}
                <span className="font-14 font-family-roboto">{org.organizationName}</span>
                {org.organizationId === currentOrgId && (
                    <CheckOutlined style={{ color: '#1677ff', marginLeft: 4 }} />
                )}
            </Space>
        ),
        onClick: () => handleSwitch(org.organizationId!),
    }));

    return (
        <>
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                disabled={switching}
            >
                <button
                    className="dropdown-text dentobook-link plain-button"
                    title={intl.formatMessage({ id: 'switch_organization' })}
                    onClick={e => e.preventDefault()}
                >
                    <Space>
                        {renderOrgAvatar(currentOrg?.avatarUrl ?? userOrganization?.avatarUrl, displayName)}
                        <span
                            className="font-14 font-family-roboto"
                            style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' }}
                        >
                            {displayName}
                        </span>
                        <DownOutlined style={{ fontSize: 10 }} />
                    </Space>
                </button>
            </Dropdown>

            {/* Full-screen overlay while switching — same pattern as language change overlay */}
            {switching && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    backdropFilter: 'blur(4px)',
                }}>
                    <Spin
                        size="large"
                        indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                    />
                </div>
            )}
        </>
    );
};

export default OrgSwitcher;
